import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${window.location.origin}/api`;

export default function DemoModal({ isOpen, onClose }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    employees: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/demo-request`, formData);
      toast.success("Demo request submitted! Our team will contact you shortly.");
      setFormData({ name: "", email: "", company: "", phone: "", employees: "", message: "" });
      onClose();
    } catch (error) {
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] bg-white border-slate-200 shadow-2xl" data-testid="demo-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Request a Demo</DialogTitle>
          <p className="text-slate-600 mt-2">
            See how KineServe can transform your IT service management.
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label">Name *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="John Doe"
                required
                className="bg-white"
                data-testid="demo-name-input"
              />
            </div>
            <div>
              <label className="form-label">Work Email *</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="john@company.com"
                required
                className="bg-white"
                data-testid="demo-email-input"
              />
            </div>
          </div>

          <div>
            <label className="form-label">Company *</label>
            <Input
              value={formData.company}
              onChange={(e) => setFormData({...formData, company: e.target.value})}
              placeholder="Your Company Name"
              required
              className="bg-white"
              data-testid="demo-company-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label">Phone</label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="+91 123 456 7890"
                className="bg-white"
                data-testid="demo-phone-input"
              />
            </div>
            <div>
              <label className="form-label">Company Size</label>
              <Select
                value={formData.employees}
                onValueChange={(value) => setFormData({...formData, employees: value})}
              >
                <SelectTrigger className="bg-white" data-testid="demo-employees-select">
                  <SelectValue placeholder="Select size" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="1-50">1-50 employees</SelectItem>
                  <SelectItem value="51-200">51-200 employees</SelectItem>
                  <SelectItem value="201-500">201-500 employees</SelectItem>
                  <SelectItem value="501-1000">501-1000 employees</SelectItem>
                  <SelectItem value="1000+">1000+ employees</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="form-label">Anything specific you'd like to see?</label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              placeholder="Tell us about your current ITSM challenges..."
              rows={3}
              className="w-full px-4 py-3 bg-white border border-slate-200 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all resize-none"
              data-testid="demo-message-input"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-slate-900 text-white hover:bg-slate-800 py-4 font-medium btn-lift"
            data-testid="demo-submit-btn"
          >
            {loading ? "Submitting..." : "Schedule Demo"}
          </Button>

          <p className="text-xs text-slate-500 text-center">
            By submitting, you agree to our Privacy Policy and Terms of Service.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
